import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DollarSign, CheckCircle2, XCircle, Clock, Search, ExternalLink, Copy, Upload } from "lucide-react";
import { HitRing } from "@/components/loading";
import { GameTabs } from "@/components/GameTabs";
import { PaymentMethodBadge } from "@/components/PaymentMethodBadge";

const BUCKETS = [
  { key: "ready",    label: "Listos para liberar", icon: DollarSign },
  { key: "held",     label: "Retenidos (en curso)", icon: Clock },
  { key: "released", label: "Liberados (historial)", icon: CheckCircle2 },
];

function fmt(n) {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
}
function shortDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return iso.slice(0, 10); }
}
async function copy(text) {
  try { await navigator.clipboard.writeText(text); toast.success("Copiado"); }
  catch { toast.error("No se pudo copiar"); }
}
async function uploadProof(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("scope", "private");
  fd.append("kind", "release_proof");
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.path;
}

export default function AdminPayouts() {
  const [bucket, setBucket] = useState("ready");
  const [q, setQ] = useState("");
  const [game, setGame] = useState("all"); // Fase 3 admin
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releaseFor, setReleaseFor] = useState(null); // the order object being released

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payouts", { params: { bucket, q: q || undefined, ...(game !== "all" ? { game } : {}) } });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket, game]);

  const orders = data?.orders || [];

  return (
    <div data-testid="admin-payouts-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl">Liberación de pagos</h2>
          <p className="text-xs text-neutral-500 mt-1">Cada pedido se libera individualmente con su propio N° de transferencia y su foto de comprobante.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input
              data-testid="payouts-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Buscar por pedido, vendedor o carta"
              className="bg-[#171717] border-[#262626] pl-8 h-9 text-sm w-72"
            />
          </div>
          <Button data-testid="payouts-refresh" onClick={load} variant="outline" size="sm" className="border-[#262626]">Actualizar</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-[#262626]" data-testid="payouts-tabs">
        {BUCKETS.map(b => (
          <button
            key={b.key}
            data-testid={`payouts-tab-${b.key}`}
            onClick={() => { setQ(""); setBucket(b.key); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-[1px] transition-colors ${
              bucket === b.key ? "border-[#EF4444] text-white" : "border-transparent text-neutral-500 hover:text-white"
            }`}
          >
            <b.icon size={14} /> {b.label}
          </button>
        ))}
      </div>

      {/* Fase 3 · filtro de juego separado del bucket */}
      <GameTabs value={game} onChange={setGame} testidPrefix="admin-payouts" className="mb-4" />

      {/* Header stats */}
      {data && (
        <div className="flex items-center flex-wrap gap-3 mb-4">
          <Badge className="bg-[#171717] border-[#262626] text-neutral-300" data-testid="payouts-count">
            {data.count} pedido(s)
          </Badge>
          <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300">
            <CheckCircle2 size={12} className="mr-1" /> {data.balanced_count} cuadran
          </Badge>
          {data.unbalanced_count > 0 && (
            <Badge className="bg-red-500/15 border-red-500/30 text-red-300" data-testid="payouts-unbalanced-count">
              <XCircle size={12} className="mr-1" /> {data.unbalanced_count} NO cuadran
            </Badge>
          )}
          <Badge className="bg-[#171717] border-[#262626] text-white ml-auto">
            Neto total: <b className="ml-1 text-[#EF4444]">{fmt(data.total_net_seller)}</b>
          </Badge>
        </div>
      )}

      {loading && (
        <div className="text-neutral-500 flex items-center gap-2 py-8"><HitRing tone="light" size={16} /> Cargando…</div>
      )}
      {!loading && orders.length === 0 && (
        <div className="text-neutral-500 py-8 text-center bg-[#0A0A0A] border border-[#262626] rounded-md" data-testid="payouts-empty">
          No hay pedidos en este estado.
        </div>
      )}

      <div className="space-y-3">
        {orders.map(o => (
          <PayoutRow
            key={o.id}
            o={o}
            bucket={bucket}
            onRelease={() => setReleaseFor(o)}
          />
        ))}
      </div>

      {releaseFor && (
        <ReleaseModal
          order={releaseFor}
          onClose={() => setReleaseFor(null)}
          onDone={() => { setReleaseFor(null); load(); }}
        />
      )}
    </div>
  );
}

function PayoutRow({ o, bucket, onRelease }) {
  const b = o.breakdown;
  const cuadraOk = b.cuadre.ok;
  const [expanded, setExpanded] = useState(false);
  const pi = o.seller?.payout_info;
  const isReady = bucket === "ready";
  const missingPayout = !pi;
  const canRelease = isReady && cuadraOk && !missingPayout;

  return (
    <div
      data-testid={`payout-row-${o.id}`}
      className={`bg-[#111] border rounded-md p-4 ${!cuadraOk ? "border-red-500/40" : "border-[#262626]"}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {o.auction_id && (
            <Badge className="bg-[#FF4438]/15 border-[#FF4438]/40 text-[#ff8a80]" data-testid={`payout-auction-${o.id}`}>SUBASTA</Badge>
          )}
          <span className="font-mono text-xs text-neutral-500">{o.auction_id ? "Subasta " : ""}#{o.short_id}</span>
          <span className={`status-pill status-${o.status.toLowerCase().replace("_", "-")}`}>{o.status}</span>
          {o.auto_closed && (
            <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300" data-testid={`payout-auto-${o.id}`}>Cierre automático</Badge>
          )}
          {cuadraOk ? (
            <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300" data-testid={`payout-cuadre-${o.id}`}>
              <CheckCircle2 size={12} className="mr-1" /> CUADRA
            </Badge>
          ) : (
            <Badge className="bg-red-500/15 border-red-500/30 text-red-300" data-testid={`payout-cuadre-${o.id}`}>
              <XCircle size={12} className="mr-1" /> NO CUADRA (Δ {fmt(b.cuadre.diff)})
            </Badge>
          )}
          {isReady && missingPayout && (
            <Badge className="bg-red-500/15 border-red-500/30 text-red-300" data-testid={`payout-missing-info-${o.id}`}>
              Sin datos de cobro
            </Badge>
          )}
          <a
            href={`/order/${o.id}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`payout-open-${o.id}`}
            className="ml-auto text-xs text-neutral-400 hover:text-white flex items-center gap-1"
          >{o.auction_id ? "Abrir subasta" : "Abrir pedido"} <ExternalLink size={10} /></a>
        </div>

        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs">
          <div><span className="text-neutral-500">Vendedor:</span> <b className="text-white">{o.seller?.store_name || o.seller?.name || "—"}</b></div>
          <div><span className="text-neutral-500">Comprador:</span> <b className="text-white">{o.buyer?.name || "—"}</b></div>
          <div><span className="text-neutral-500">{o.auction_id ? "Subasta:" : "Carta:"}</span> <span className="text-neutral-200">{o.item_count > 1 ? `${o.item_count} cartas` : (o.card_name || "—")}</span></div>
          <div><span className="text-neutral-500">Creado:</span> <span className="text-neutral-200">{shortDate(o.created_at)}</span></div>
        </div>

        <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <div><span className="text-neutral-500 text-xs mr-1">Pagado:</span><b className="text-white">{fmt(b.buyer_paid)}</b></div>
          <div><span className="text-neutral-500 text-xs mr-1">Método:</span><PaymentMethodBadge method={o.payment_method} size="sm" /></div>
          <div><span className="text-neutral-500 text-xs mr-1">Comisión HitTCG:</span><span>{fmt(b.commission)}</span></div>
          <div><span className="text-neutral-500 text-xs mr-1">Payphone:</span><span>{fmt(b.payphone_fee)}</span></div>
          <div><span className="text-neutral-500 text-xs mr-1">Envío:</span><span>{fmt(b.shipping)}</span></div>
          <div className="ml-auto"><span className="text-neutral-500 text-xs mr-1">Neto al vendedor:</span><b className="text-emerald-400" data-testid={`payout-net-${o.id}`}>{fmt(b.net_to_transfer)}</b>{b.is_interbank && b.interbank_fee > 0 && (<span className="text-xs text-amber-300 ml-1" data-testid={`payout-interbank-${o.id}`}>(−{fmt(b.interbank_fee)} interbancario)</span>)}</div>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            data-testid={`payout-expand-${o.id}`}
            onClick={() => setExpanded(x => !x)}
            className="text-xs text-neutral-500 hover:text-white underline"
          >{expanded ? "Ocultar" : "Ver"} datos de cobro y desglose completo</button>
          {isReady && (
            <Button
              data-testid={`payout-release-${o.id}`}
              onClick={onRelease}
              disabled={!canRelease}
              title={
                !cuadraOk ? "El pedido no cuadra — revisa los montos" :
                missingPayout ? "El vendedor no tiene datos de cobro cargados" : ""
              }
              className="ml-auto bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-xs"
            >{o.auction_id ? "Liberar esta subasta" : "Liberar este pedido"}</Button>
          )}
        </div>

        {expanded && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3">
              <div className="text-[10px] uppercase text-neutral-500 tracking-widest mb-2">Datos de cobro del vendedor</div>
              {pi ? (
                <div className="text-xs space-y-1" data-testid={`payout-info-${o.id}`}>
                  <div><span className="text-neutral-500">Banco:</span> <b className="text-white">{pi.bank_name}</b> <span className="text-neutral-500">({pi.account_type})</span></div>
                  <div className="flex items-center gap-1">
                    <span className="text-neutral-500">Cuenta:</span>
                    <b className="text-white font-mono">{pi.account_number}</b>
                    <button type="button" onClick={() => copy(pi.account_number)} className="text-neutral-500 hover:text-white" title="Copiar"><Copy size={11} /></button>
                  </div>
                  <div><span className="text-neutral-500">Titular:</span> <b className="text-white">{pi.holder_name}</b></div>
                  <div><span className="text-neutral-500">Cédula titular:</span> <span className="text-white font-mono">{pi.holder_cedula}</span></div>
                  <div className="pt-1"><span className="text-neutral-500">Email:</span> {o.seller?.email}</div>
                  {o.seller?.phone && <div><span className="text-neutral-500">Teléfono:</span> {o.seller.phone}</div>}
                </div>
              ) : (
                <div className="text-xs text-red-300">El vendedor no tiene datos de cobro cargados. No se puede transferir.</div>
              )}
            </div>

            <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3">
              <div className="text-[10px] uppercase text-neutral-500 tracking-widest mb-2">Desglose del pedido</div>
              <div className="text-xs space-y-1">
                <div className="flex items-center justify-between pb-1 border-b border-[#262626]">
                  <span className="text-neutral-400">Método de pago</span>
                  <PaymentMethodBadge method={o.payment_method} size="sm" />
                </div>
                <Row k="Precio de venta" v={fmt(b.price)} />
                <Row k="Envío" v={fmt(b.shipping)} />
                <Row k="Comisión HitTCG" v={fmt(b.commission)} />
                <Row k={`Comisión Payphone${b.payphone_fee_derived ? " (derivada)" : ""}`} v={fmt(b.payphone_fee)} />
                {Number(b.payphone_fee || 0) === 0 && o.payment_method === "transfer" && (
                  <div className="text-[10px] text-blue-300/80 italic">
                    Sin comisión Payphone (transferencia bancaria).
                  </div>
                )}
                <div className="border-t border-[#262626] my-1"></div>
                <Row k="Total pagado por comprador" v={fmt(b.buyer_paid)} bold />
                <Row k="Neto vendedor (incluye envío, antes de interbancario)" v={fmt(b.net_seller)} highlight />
                {!cuadraOk && (
                  <div className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
                    Cuadre esperado: {fmt(b.cuadre.expected)} · calculado: {fmt(b.cuadre.computed)} · diferencia: <b>{fmt(b.cuadre.diff)}</b>
                  </div>
                )}
              </div>
            </div>

            {o.release_receipt && (
              <div className="md:col-span-2 bg-[#0A0A0A] border border-emerald-500/30 rounded p-3" data-testid={`payout-receipt-${o.id}`}>
                <div className="text-[10px] uppercase text-emerald-400 tracking-widest mb-2">Comprobante de transferencia registrado</div>
                <div className="text-xs grid grid-cols-2 gap-x-6 gap-y-1">
                  <div><span className="text-neutral-500">N° comprobante:</span> <b className="text-white font-mono">{o.release_receipt.number}</b></div>
                  <div><span className="text-neutral-500">Fecha:</span> <b className="text-white">{o.release_receipt.date}</b></div>
                  <div><span className="text-neutral-500">Emitido:</span> {shortDate(o.release_receipt.issued_at)}</div>
                  {o.release_receipt.proof_path && (
                    <div><a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${o.release_receipt.proof_path}`} target="_blank" rel="noreferrer" data-testid={`payout-proof-${o.id}`} className="text-emerald-300 hover:underline flex items-center gap-1"><Upload size={11} /> Ver comprobante</a></div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReleaseModal({ order, onClose, onDone }) {
  const [receiptNumber, setReceiptNumber] = useState("");
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!receiptNumber.trim()) { toast.error("Ingresa el N° de comprobante"); return; }
    if (!receiptDate) { toast.error("Ingresa la fecha de la transferencia"); return; }
    if (!file) { toast.error("Adjunta la foto/PDF del comprobante"); return; }
    setLoading(true);
    try {
      const proofPath = await uploadProof(file);
      await api.post(`/admin/orders/${order.id}/release`, {
        receipt_number: receiptNumber.trim(),
        receipt_date: receiptDate,
        transfer_proof_path: proofPath,
      });
      toast.success(`${order.auction_id ? "Subasta" : "Pedido"} #${order.short_id} liberado y notificado al vendedor`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo liberar");
    } finally { setLoading(false); }
  };

  const b = order.breakdown;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="payouts-release-modal">
      <div className="bg-[#111] border border-[#262626] rounded-md max-w-lg w-full p-6">
        <h3 className="font-display text-xl mb-1">Liberar {order.auction_id ? "subasta" : "pedido"} #{order.short_id}</h3>
        <p className="text-xs text-neutral-500 mb-4">
          Transferir <b className="text-emerald-400">{fmt(b.net_to_transfer)}</b> a <b className="text-white">{order.seller?.store_name || order.seller?.name}</b>{b.is_interbank && b.interbank_fee > 0 && (<> · descuento interbancario aplicado <b className="text-amber-300">−{fmt(b.interbank_fee)}</b></>)}. Sube el comprobante de ESTA transferencia.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-neutral-400">Número de comprobante / transferencia *</Label>
            <Input
              data-testid="payouts-receipt-number"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              placeholder="Ej. TRF-20260215-0034"
              className="bg-[#171717] border-[#262626]"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Fecha de la transferencia *</Label>
            <Input
              data-testid="payouts-receipt-date"
              type="date"
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
              className="bg-[#171717] border-[#262626]"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Foto del comprobante *</Label>
            <label className="flex items-center gap-3 p-3 bg-[#171717] border border-dashed border-[#262626] rounded-md cursor-pointer hover:border-emerald-500 mt-1">
              <Upload size={16} className="text-neutral-500" />
              <span className="text-sm text-neutral-300 flex-1">{file ? file.name : "Adjuntar comprobante de esta transferencia"}</span>
              <input
                data-testid="payouts-receipt-file"
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button
            data-testid="payouts-confirm-release"
            onClick={submit}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 flex-1"
          >
            {loading ? <span className="mr-2 inline-flex"><HitRing tone="light" size={14} /></span> : null}
            Confirmar y notificar al vendedor
          </Button>
          <Button variant="outline" onClick={onClose} disabled={loading} className="border-[#262626]">Cancelar</Button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, bold, highlight }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-neutral-400">{k}</span>
      <span className={`${bold ? "text-white font-semibold" : highlight ? "text-emerald-400 font-semibold" : "text-neutral-200"}`}>{v}</span>
    </div>
  );
}
